import React, { useState } from 'react';
import { FaEdit, FaTrash, FaEye, FaSync } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Pie, Doughnut, Scatter } from 'react-chartjs-2';
import ScatterChart from './ScatterChart';
import DonutChart from './DonutChart';
import { formatDateTime } from '../../../utils/formatUtils';
import { appendAlphaIfHex } from './chartPicklistColors';
import {
  extractChartSeries,
  seriesToChartJsDatasets,
} from './adapters/extractChartSeries';
import { resolveChartType } from './echarts/chartTypeRegistry';
import './ChartDisplay.css';

const truncateCategoryLabel = (text, maxLen = 20) => {
  const s = String(text ?? '');
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const formatCompactAxisNumber = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';
  const sign = n < 0 ? '-' : '';
  const v = Math.abs(n);
  if (v >= 1e9) return `${sign}${(v / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (v >= 1e6) return `${sign}${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1e4) return `${sign}${(v / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  if (v >= 1000) return `${sign}${Math.round(v).toLocaleString()}`;
  if (Number.isInteger(n)) return `${sign}${Math.round(v)}`;
  return `${sign}${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const parseChartDataValue = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'object' && value !== null && 'y' in value) {
    return parseChartDataValue(value.y);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t === 'N/A') return NaN;
    const cleaned = t.replace(/[₹$€£,\s]/g, '').replace(/%$/, '');
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : NaN;
  }
  return NaN;
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ChartDisplay = ({
  chart,
  onEdit,
  onDelete,
  onView,
  isPreview = false,
  selectedFields = [],
  summarizedFields = {},
  selectedGroups = [],
  summarizedData = {}, // Add summarizedData prop
  previewData = [], // Add previewData for record count calculation
  displayAuditInfo = false // Add displayAuditInfo prop
}) => {
  // Bump to re-run render from latest props (no synthetic/dummy data)
  const [, setRenderNonce] = useState(0);
  const handleRefresh = () => setRenderNonce((n) => n + 1);

  const extractDataFromSummarizedData = () => {
    const extracted = extractChartSeries({
      chart,
      summarizedData,
      previewData,
      selectedFields,
      summarizedFields,
    });
    if (!extracted) return null;

    const datasets = seriesToChartJsDatasets(extracted, chart);
    return {
      labels: extracted.labels,
      datasets,
      groupFieldInfo: extracted.groupFieldInfo,
      selectedValueFields: extracted.selectedValueFields,
      xAxisLabel: extracted.xAxisLabel,
      yAxisLabel: extracted.yAxisLabel,
    };
  };

  // Create Y-axis label with aggregation info
  const createYAxisLabel = (fieldName) => {
    if (!fieldName) return 'Value';

    const field = selectedFields.find(f => f.field_name === fieldName);
    if (!field) return fieldName;

    const baseLabel = field.field_label || field.field_name;
    const isSummarized = summarizedFields[field.id];

    if (isSummarized && summarizedFields[field.id].aggregation) {
      const aggregation = summarizedFields[field.id].aggregation;
      return `${baseLabel} (${aggregation})`;
    }

    return baseLabel;
  };

  const renderChart = () => {
    try {
      const resolvedChartType = resolveChartType(chart);
      // previewData is often a row[] from reports (for record count / filters). That is NOT Chart.js
      // format; treating it as truthy used to take the "single dataset" path and hardcode one bar color (#36A2EB).
      // Always prefer building from summarizedData so category picklist / path colors apply.
      let chartData;

      const isChartjsPreviewObject = (p) =>
        p != null &&
        typeof p === 'object' &&
        !Array.isArray(p) &&
        (Array.isArray(p.datasets) || (Array.isArray(p.labels) && p.data !== undefined));

      const extractedData = extractDataFromSummarizedData();
      const canUseExtract =
        extractedData &&
        Array.isArray(extractedData.labels) &&
        extractedData.labels.length > 0 &&
        Array.isArray(extractedData.datasets) &&
        extractedData.datasets.length > 0;

      if (canUseExtract) {
        console.log('ChartDisplay: Using extracted data from summarizedData:', extractedData);
        chartData = {
          labels: extractedData.labels,
          datasets: extractedData.datasets
        };
      } else if (isChartjsPreviewObject(chart.previewData)) {
        // Saved Chart.js-style preview (e.g. modal) — not a row array
        console.log('ChartDisplay: Using previewData (Chart.js object):', chart.previewData);
        if (chart.previewData.datasets && Array.isArray(chart.previewData.datasets)) {
          console.log('ChartDisplay: Using multiple datasets structure');
          chartData = {
            labels: Array.isArray(chart.previewData.labels) ? chart.previewData.labels : [],
            datasets: chart.previewData.datasets.map((dataset) => ({
              ...dataset,
              backgroundColor: chart.chartType === 'donut' || chart.chartType === 'area'
                ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', '#9966FF']
                : dataset.backgroundColor,
              borderColor: chart.chartType === 'line' || chart.chartType === 'area'
                ? dataset.borderColor
                : undefined,
              borderWidth: chart.chartType === 'line' || chart.chartType === 'area' ? 2 : (dataset.borderWidth || 1),
              fill: chart.chartType === 'area'
            }))
          };
        } else {
          console.log('ChartDisplay: Using single-dataset previewData structure');
          chartData = {
            labels: Array.isArray(chart.previewData.labels) ? chart.previewData.labels : [],
            datasets: [{
              label: chart.previewData.yAxisTitle || 'Values',
              data: Array.isArray(chart.previewData.data) ? chart.previewData.data : [],
              backgroundColor: chart.chartType === 'pie' || chart.chartType === 'donut' || chart.chartType === 'area'
                ? ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', '#9966FF']
                : '#36A2EB',
              borderColor: chart.chartType === 'line' || chart.chartType === 'area'
                ? '#36A2EB'
                : undefined,
              borderWidth: chart.chartType === 'line' || chart.chartType === 'area' ? 2 : undefined,
              fill: chart.chartType === 'area'
            }]
          };
        }
      } else {
        console.log('ChartDisplay: No chartable extract or Chart.js preview; empty series');
        chartData = { labels: [], datasets: [] };
      }

      console.log('ChartDisplay: Final chartData:', chartData);

      if (!chartData || !Array.isArray(chartData.datasets)) {
        console.error('ChartDisplay: Invalid chartData structure:', chartData);
        chartData = { labels: [], datasets: [] };
      }

      const isDonutOrScatter = resolvedChartType === 'donut' || resolvedChartType === 'scatter';
      const isTable = resolvedChartType === 'table';
      const hasSeries =
        Array.isArray(chartData.labels) &&
        chartData.labels.length > 0 &&
        Array.isArray(chartData.datasets) &&
        chartData.datasets.length > 0 &&
        chartData.datasets.some(
          (ds) => Array.isArray(ds.data) && ds.data.length > 0
        );
      if (!isDonutOrScatter && !isTable && !hasSeries) {
        return (
          <div className="chart-container">
            {chart.title ? <div className="chart-title">{chart.title}</div> : null}
            <div className="chart-placeholder" style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}>
              No data available
            </div>
          </div>
        );
      }

      const getMaxValueFromDatasets = (datasets) => {
        let max = 0;
        datasets.forEach(dataset => {
          (dataset.data || []).forEach(value => {
            const numericValue = parseChartDataValue(value);
            if (Number.isFinite(numericValue)) {
              max = Math.max(max, numericValue);
            }
          });
        });
        return max;
      };

      const getPaddedMax = (maxValue) => {
        if (!Number.isFinite(maxValue) || maxValue <= 0) {
          return undefined;
        }
        if (maxValue <= 5) {
          return maxValue + 1;
        }
        if (maxValue <= 10) {
          return maxValue + 2;
        }
        if (maxValue <= 100) {
          return Math.ceil(maxValue * 1.1);
        }
        return Math.ceil(maxValue * 1.05);
      };

      const paddedMaxValue = getPaddedMax(getMaxValueFromDatasets(chartData.datasets));
      const labelCount = Array.isArray(chartData.labels) ? chartData.labels.length : 0;
      const categoryTickMaxChars = labelCount > 14 ? 14 : labelCount > 8 ? 18 : 28;
      const rotateCategories = labelCount > 6;
      const isArcChart = resolvedChartType === 'pie' || resolvedChartType === 'donut';

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: isArcChart
            ? { top: 8, right: 12, bottom: 8, left: 12 }
            : {
                top: 4,
                right: 6,
                bottom: rotateCategories ? 10 : 4,
                left: 4
              }
        },
        datasets: {
          bar: {
            barPercentage: labelCount > 18 ? 0.55 : labelCount > 10 ? 0.65 : 0.78,
            categoryPercentage: labelCount > 18 ? 0.65 : 0.78
          }
        },
        elements: {
          bar: {
            borderRadius: 6,
            borderSkipped: false
          }
        },
        plugins: {
          legend: {
            position: chart.legendPosition || 'top',
            display: chart.chartType !== 'pie' && chart.chartType !== 'donut'
          },
          title: {
            display: false // We'll show title separately
          },
          tooltip: {
            enabled: chart.showTooltips !== undefined ? chart.showTooltips : true,
            callbacks: {
              title: (items) => {
                if (!items?.length) return '';
                const idx = items[0].dataIndex;
                const raw =
                  chartData.labels && chartData.labels[idx] !== undefined
                    ? chartData.labels[idx]
                    : items[0].label;
                return raw != null ? String(raw) : '';
              },
              label: function (context) {
                const raw =
                  context.parsed?.y != null
                    ? context.parsed.y
                    : context.parsed?.x;
                const value = typeof raw === 'number' ? raw : parseChartDataValue(raw);
                const formatted = Number.isFinite(value) ? value.toLocaleString() : String(raw);
                return `${context.dataset.label}: ${formatted}`;
              }
            }
          },
          datalabels: {
            display: function (context) {
              // For pie/donut charts, only show labels for significant slices
              if (chart.chartType === 'pie' || chart.chartType === 'donut') {
                const data = context.dataset.data;
                const dataIndex = context.dataIndex;
                const value = data[dataIndex];
                const total = data.reduce((a, b) => a + b, 0);
                const percentage = (value / total) * 100;

                // Only show if > 5% and showLabels is enabled
                return (chart.showLabels !== undefined ? chart.showLabels : false) && percentage > 5;
              }

              return chart.showLabels !== undefined ? chart.showLabels : false;
            },
            anchor: function (context) {
              if (isArcChart) {
                return 'center';
              }
              return 'center';
            },
            align: function (context) {
              if (isArcChart) {
                return 'center';
              }

              // Get the chart instance and data
              const chartInstance = context.chart;
              const data = context.dataset.data;
              const dataIndex = context.dataIndex;
              const value = data[dataIndex];

              // Get the y-axis scale to determine position
              const yScale = chartInstance.scales.y;
              if (!yScale) return 'top';

              const yPosition = yScale.getPixelForValue(value);
              const chartHeight = yScale.height;
              const topThreshold = chartHeight * 0.2; // 20% from top

              // If the data point is in the top 20% of the chart, position label below
              if (yPosition < topThreshold) {
                return 'bottom';
              }
              // Otherwise, position label above
              return 'top';
            },
            color: chart.labelColor || '#000000',
            font: {
              weight: '600',
              size: 10
            },
            formatter: function (value, context) {
              if (chart.chartType === 'pie' || chart.chartType === 'donut') {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${percentage}%`;
              }
              const n = parseChartDataValue(value);
              return Number.isFinite(n) ? n.toLocaleString() : String(value ?? '');
            },
            offset: isArcChart ? 0 : 8,
            padding: 2,
            clamp: true,
            clip: true
          }
        },
        scales: chart.chartType !== 'pie' && chart.chartType !== 'donut' && chart.chartType !== 'table' ? {
          x: {
            title: {
              display: true,
              text: chart.xAxisLabel || chart.previewData?.xAxisTitle || 'Categories',
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              autoSkip: true,
              autoSkipPadding: 12,
              maxRotation: rotateCategories ? 55 : 0,
              minRotation: rotateCategories ? 40 : 0,
              maxTicksLimit: Math.min(16, Math.max(6, labelCount)),
              font: { size: labelCount > 12 ? 10 : 11 },
              callback: function (value) {
                const label = this.getLabelForValue(value);
                return truncateCategoryLabel(label, categoryTickMaxChars);
              }
            },
            grid: {
              display: labelCount <= 24
            }
          },
          y: {
            beginAtZero: true,
            ...(paddedMaxValue !== undefined ? { max: paddedMaxValue } : { grace: '8%' }),
            title: {
              display: true,
              text: chart.yAxisLabel || chart.previewData?.yAxisTitle || 'Values',
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              maxTicksLimit: 6,
              precision: 0,
              callback: (value) => formatCompactAxisNumber(value)
            }
          }
        } : undefined
      };

      switch (resolvedChartType) {
        case 'bar':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={options} />
              </div>
            </div>
          );

        case 'stackedColumn': {
          const stackedColumnData = {
            ...chartData,
            datasets: chartData.datasets.map((ds) => ({ ...ds, stack: 'stack0' })),
          };
          const stackedColumnOptions = {
            ...options,
            scales: {
              x: { ...options.scales?.x, stacked: true },
              y: { ...options.scales?.y, stacked: true, beginAtZero: true },
            },
            plugins: {
              ...options.plugins,
              legend: { ...options.plugins.legend, position: 'bottom' },
            },
          };
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={stackedColumnData} options={stackedColumnOptions} />
              </div>
            </div>
          );
        }

        case 'stackedBar': {
          const stackedBarData = {
            ...chartData,
            datasets: chartData.datasets.map((ds) => ({ ...ds, stack: 'stack0' })),
          };
          const stackedBarOptions = {
            ...options,
            indexAxis: 'y',
            scales: {
              x: { stacked: true, beginAtZero: true },
              y: { stacked: true },
            },
            plugins: {
              ...options.plugins,
              legend: { ...options.plugins.legend, position: 'bottom' },
            },
          };
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={stackedBarData} options={stackedBarOptions} />
              </div>
            </div>
          );
        }

        case 'horizontalBar':
          // Enhanced options for horizontal bar charts with consistent bar thickness
          const horizontalBarOptions = {
            ...options,
            indexAxis: 'y',
            elements: {
              bar: {
                borderWidth: 1,
                borderSkipped: false,
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                ...(paddedMaxValue !== undefined ? { max: paddedMaxValue } : { grace: '8%' }),
                grid: {
                  display: true
                },
                ticks: {
                  maxTicksLimit: 6,
                  callback: (value) => formatCompactAxisNumber(value)
                },
                title: {
                  display: true,
                  text: chart.yAxisLabel || chart.previewData?.yAxisTitle || 'Values',
                  font: { size: 12, weight: 'bold' }
                }
              },
              y: {
                grid: {
                  display: false
                },
                title: {
                  display: true,
                  text: chart.xAxisLabel || chart.previewData?.xAxisTitle || 'Categories',
                  font: { size: 12, weight: 'bold' }
                },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: Math.min(18, Math.max(8, labelCount)),
                  font: { size: labelCount > 12 ? 10 : 11 },
                  callback: function (value) {
                    const label = this.getLabelForValue(value);
                    return truncateCategoryLabel(label, categoryTickMaxChars);
                  }
                }
              }
            },
            plugins: {
              ...options.plugins,
              legend: {
                ...options.plugins.legend,
                position: 'bottom'
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={horizontalBarOptions} />
              </div>
            </div>
          );

        case 'line':
          // Enhanced options for line charts
          const lineOptions = {
            ...options,
            elements: {
              line: {
                tension: 0.4, // Smooth curves
                borderWidth: 3,
                fill: false
              },
              point: {
                radius: 4,
                hoverRadius: 6,
                borderWidth: 2
              }
            },
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              ...options.plugins,
              tooltip: {
                ...options.plugins.tooltip,
                mode: 'index',
                intersect: false
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Line data={chartData} options={lineOptions} />
              </div>
            </div>
          );

        case 'scatter':
          // Create a chartConfig object that matches what ScatterChart expects
          const chartConfig = {
            title: chart.title,
            scatterXAxis: chart.scatterXAxis,
            scatterYAxis: chart.scatterYAxis,
            scatterGroupField: chart.scatterGroupField,
            scatterXAxisLabel: chart.scatterXAxisLabel,
            scatterYAxisLabel: chart.scatterYAxisLabel,
            legendPosition: chart.legendPosition,
            showTooltips: chart.showTooltips,
            showLabels: chart.showLabels,
            labelColor: chart.labelColor
          };

          return (
            <ScatterChart
              chartConfig={chartConfig}
              chartTitle={chart.title}
              summarizedData={summarizedData}
              chartData={chartData}
            />
          );

        case 'pie':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Pie data={chartData} options={options} />
              </div>
            </div>
          );

        case 'donut':
          // Create a chartConfig object that matches what DonutChart expects
          const donutChartConfig = {
            title: chart.title,
            chartType: 'donut',
            pieValueField: chart.pieValueField,
            pieSliceField: chart.pieSliceField,
            showValues: chart.showValues,
            showPercentages: chart.showPercentages,
            showTotal: chart.showTotal,
            legendColors: chart.legendColors,
            legendPosition: chart.legendPosition,
            valueFieldType: chart.valueFieldType,
            showTooltips: chart.showTooltips
          };

          return (
            <DonutChart
              chartConfig={donutChartConfig}
              chartTitle={chart.title}
              summarizedData={summarizedData}
              chartData={chartData}
              previewData={previewData}
              selectedGroups={selectedGroups}
            />
          );

        case 'area':
          // Create proper area chart data with fill configurations
          const areaChartData = {
            ...chartData,
            datasets: chartData.datasets.map(dataset => {
              const bg = dataset.backgroundColor;
              const withFill = Array.isArray(bg)
                ? bg
                : (typeof bg === 'string' && bg.startsWith('#') && bg.length === 7
                  ? appendAlphaIfHex(bg, '40')
                  : typeof bg === 'string'
                    ? bg + '40'
                    : bg);
              return {
                ...dataset,
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 3,
                pointHoverRadius: 5,
                backgroundColor: withFill,
                borderColor: dataset.borderColor
              };
            })
          };

          // Enhanced options for area charts
          const areaOptions = {
            ...options,
            elements: {
              line: {
                tension: 0.4, // Smooth curves
                borderWidth: 2
              },
              point: {
                radius: 3,
                hoverRadius: 5,
                borderWidth: 2
              }
            },
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              ...options.plugins,
              tooltip: {
                ...options.plugins.tooltip,
                mode: 'index',
                intersect: false
              }
            },
            scales: {
              ...options.scales,
              x: {
                ...options.scales?.x,
                stacked: false // Allow overlapping areas
              },
              y: {
                ...options.scales?.y,
                stacked: false, // Allow overlapping areas
                beginAtZero: true
              }
            }
          };

          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Line data={areaChartData} options={areaOptions} />
              </div>
            </div>
          );

        case 'table':
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="table-render">
                {renderTable()}
              </div>
            </div>
          );

        default:
          return (
            <div className="chart-container">
              <div className="chart-title">{chart.title}</div>
              <div className="chart-render">
                <Bar data={chartData} options={options} />
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('ChartDisplay: Error rendering chart:', error);
      return (
        <div className="chart-error">
          <div className="error-icon">⚠️</div>
          <p>Error rendering chart</p>
          <small>{error.message}</small>
        </div>
      );
    }
  };

  const renderTable = () => {
    let chartData;

    if (chart.previewData) {
      chartData = {
        labels: chart.previewData.labels || [],
        datasets: [{
          data: chart.previewData.data || []
        }]
      };
    } else {
      const extractedData = extractDataFromSummarizedData();
      if (extractedData) {
        chartData = {
          labels: extractedData.labels,
          datasets: extractedData.datasets
        };
      } else {
        chartData = { labels: [], datasets: [] };
      }
    }

    // Handle both single and multiple Y-axis fields
    const yAxisFields = chart.yAxisFields || (chart.yAxisField ? [chart.yAxisField] : []);

    const tableData = chartData.labels.map((label, index) => {
      const row = { [chart.xAxisField || 'Category']: label };

      // Add data for each Y-axis field
      yAxisFields.forEach((fieldName, fieldIndex) => {
        const dataset = chartData.datasets[fieldIndex];
        row[fieldName] = dataset?.data[index] || 0;
      });

      return row;
    });

    return (
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{chart.xAxisField || 'Category'}</th>
              {yAxisFields.map((fieldName, index) => (
                <th key={index}>{createYAxisLabel(fieldName)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index}>
                <td>{row[chart.xAxisField || 'Category']}</td>
                {yAxisFields.map((fieldName, fieldIndex) => (
                  <td key={fieldIndex}>{row[fieldName]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={`chart-display ${isPreview ? 'preview' : ''}`}>
      <div className="chart-content">
        {renderChart()}

        {chart.description && (
          <div className="chart-description">
            {chart.description}
          </div>
        )}

        {displayAuditInfo && (
          <div className="chart-metadata">
            <div className="chart-type-badge">
              {(() => {
                const chartTypeLabels = {
                  'bar': 'Column',
                  'stackedColumn': 'Stacked Column',
                  'horizontalBar': 'Bar',
                  'stackedBar': 'Stacked Bar',
                  'line': 'Line',
                  'scatter': 'Scatter',
                  'pie': 'Pie',
                  'donut': 'Donut',
                  'area': 'Area',
                  'mixed': 'Mixed',
                  'funnel': 'Funnel',
                  'table': 'Table'
                };
                return `${chartTypeLabels[chart.chartType] || chart.chartType} Chart`;
              })()}
            </div>

            {/* Chart Audit Information */}
            {(chart.createddate || chart.createdby || chart.lastmodifieddate || chart.lastmodifiedby) && (
              <div className="chart-audit-info">
                {chart.createdby && (
                  <div className="audit-item">
                    <span className="audit-label">Created by:</span>
                    <span className="audit-value">{chart.createdby}</span>
                  </div>
                )}
                {chart.createddate && (
                  <div className="audit-item">
                    <span className="audit-label">Created:</span>
                    <span className="audit-value">{formatDateTime(chart.createddate)}</span>
                  </div>
                )}
                {chart.lastmodifiedby && (
                  <div className="audit-item">
                    <span className="audit-label">Modified by:</span>
                    <span className="audit-value">{chart.lastmodifiedby}</span>
                  </div>
                )}
                {chart.lastmodifieddate && (
                  <div className="audit-item">
                    <span className="audit-label">Modified:</span>
                    <span className="audit-value">{formatDateTime(chart.lastmodifieddate)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Show view button in preview mode, all buttons in edit mode */}
      {(!isPreview || onView) && (
        <div className="chart-actions">
          {!isPreview && (
            <>
              <button
                className="chart-action-btn refresh-btn"
                onClick={handleRefresh}
                title="Refresh Data"
              >
                <FaSync />
              </button>
              <button
                className="chart-action-btn edit-btn"
                onClick={() => onEdit && onEdit(chart)}
                title="Edit Chart"
              >
                <FaEdit />
              </button>
              <button
                className="chart-action-btn delete-btn"
                onClick={() => onDelete && onDelete(chart)}
                title="Delete Chart"
              >
                <FaTrash />
              </button>
            </>
          )}
          {onView && (
            <button
              className="chart-action-btn view-btn"
              onClick={() => onView(chart)}
              title="View Chart"
            >
              <FaEye />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartDisplay;
